import React, { FunctionComponent, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import _ from 'lodash';
import clsx from 'clsx';
import { MarketplaceRequirements, MarketplaceSpeciesRequirements } from '@spike/marketplace-model';
import { Button } from 'components/UI';
import { useMasterData } from 'hooks';
import { Accordion, Switch } from 'components/UI';
import { AddOther } from '../UI/AddOther';
import { OptionType } from '../PetTypes/UI/OptionType';
import { useNonInitialEffect } from '@versiondos/hooks';
import { MarketplaceStatus } from '@spike/marketplace-action';
import { BusinessSettingsTitle } from '../UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Option } from '@spike/model';

export interface Props {
    requirements: MarketplaceSpeciesRequirements;
    onSave?: (updatedRequirements: MarketplaceSpeciesRequirements) => void;
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        mobileBody: {
            overflow: 'hidden'
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.only('lg')]: {
                width: '525px'
            },
            [theme.breakpoints.only('xl')]: {
                width: '565px'
            }
        },
        containerNew: {
            [theme.breakpoints.down('md')]: {
                'width': '100%',
                'height': '100%',
                'position': 'relative',
                'paddingBottom': '145px',
                'display': 'block',
                '@supports (-webkit-touch-callout: none)': {
                    paddingBottom: '160px'
                }
            }
        },
        titleContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        businessSettingsTittle: {
            'display': 'flex',
            'alignItems': 'center',
            '& $iconBusinessTitle': {
                fontSize: '20px'
            },
            [theme.breakpoints.down('sm')]: {
                'fontSize': '20px',
                'marginBottom': '25px',
                '& $iconBusinessTitle': {
                    fontSize: '20px',
                    fontWeight: '300'
                }
            }
        },
        iconBusinessTitle: {
            position: 'relative',
            marginRight: '8px'
        },
        contentContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'width': '100%',
            '& .MuiAccordionDetails-root': {
                padding: '0px'
            }
        },
        contentContainerMobile: {
            position: 'relative'
        },
        tabs: {
            display: 'flex',
            borderBottom: '1px solid #EFEFEF',
            marginBottom: '20px'
        },
        tab: {
            background: 'transparent',
            border: 'none',
            borderBottom: '2px solid transparent',
            cursor: 'pointer',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '100%',
            padding: '10px 16px',
            color: '#BCB8AE'
        },
        activeTab: {
            borderBottomColor: '#000000',
            color: '#000000',
            fontWeight: 600
        },
        vaccinationsTitle: {
            borderBottom: 'none !important'
        },
        addOtherSpacing: {
            marginTop: '12px'
        },
        buttonContainer: {
            display: 'flex',
            width: '100%'
        },
        accordionTitle: {
            'borderBottom': '1px solid #EFEFEF',
            'padding': 0,
            'alignItems': 'center',
            '& .MuiAccordionSummary-content': {
                display: 'flex',
                justifyContent: 'space-between',
                [theme.breakpoints.down('sm')]: {
                    paddingTop: '20px',
                    fontSize: '6px',
                    width: '100%'
                },
                [theme.breakpoints.only('md')]: {
                    paddingTop: '20px',
                    fontSize: '16px',
                    width: '100%'
                },
                [theme.breakpoints.only('lg')]: {
                    fontSize: '16px'
                },
                [theme.breakpoints.only('xl')]: {
                    fontSize: '16px'
                }
            },
            '&.MuiAccordionSummary-root.Mui-expanded': {
                minHeight: 'inherit',
                transform: 'rotate(0deg)',
                [theme.breakpoints.down('sm')]: {
                    display: 'flex',
                    flexDirection: 'row',
                    border: 'none'
                },
                [theme.breakpoints.only('md')]: {
                    display: 'flex',
                    flexDirection: 'row',
                    border: 'none'
                }
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
                [theme.breakpoints.down('sm')]: {
                    margin: '12px 0'
                },
                [theme.breakpoints.only('md')]: {
                    margin: '6px 0'
                },
                [theme.breakpoints.only('lg')]: {
                    margin: '8px 0'
                },
                [theme.breakpoints.only('xl')]: {
                    margin: '12px 0'
                }
            },
            [theme.breakpoints.down('sm')]: {
                minHeight: '46px',
                border: 'none'
            }
        },
        titleSection: {
            fontWeight: 500,
            [theme.breakpoints.down('md')]: {
                fontSize: '16px',
                lineHeight: '100%',
                marginBottom: '5px'
            },
            [theme.breakpoints.only('lg')]: {
                lineHeight: '16px'
            },
            [theme.breakpoints.only('xl')]: {
                lineHeight: '16px'
            }
        },
        requirements: {
            '& .MuiCheckbox-root': {
                paddingLeft: '0px',
                [theme.breakpoints.down('sm')]: {
                    fontSize: '6px',
                    paddingRight: '4px !important'
                },
                [theme.breakpoints.only('md')]: {
                    fontSize: '16px',
                    paddingRight: '6px !important'
                },
                [theme.breakpoints.only('lg')]: {
                    fontSize: '16px',
                    paddingRight: '8px !important'
                },
                [theme.breakpoints.only('xl')]: {
                    fontSize: '16px',
                    paddingRight: '12px !important'
                }
            },
            '& .MuiTypography-root': {
                [theme.breakpoints.down('sm')]: {
                    fontSize: '6px'
                },
                [theme.breakpoints.only('md')]: {
                    fontSize: '16px'
                },
                [theme.breakpoints.only('lg')]: {
                    fontSize: '16px'
                },
                [theme.breakpoints.only('xl')]: {
                    fontSize: '16px'
                }
            },
            [theme.breakpoints.down('sm')]: {
                marginBottom: '7px'
            },
            [theme.breakpoints.only('md')]: {
                marginBottom: '12px'
            },
            [theme.breakpoints.only('lg')]: {
                marginBottom: '16px'
            },
            [theme.breakpoints.only('xl')]: {
                marginBottom: '24px'
            }
        },
        addOtherContainer: {
            [theme.breakpoints.only('lg')]: {
                width: '198px'
            },
            [theme.breakpoints.only('xl')]: {
                width: '300px'
            },

            '& .MuiInputBase-root': {
                padding: '0px !important'
            },
            [theme.breakpoints.down('md')]: {
                '& p': {},
                '& .MuiOutlinedInput-notchedOutline': {
                    border: 'solid 1px #D3D3D3 !important',
                    height: '40px',
                    borderRadius: '25px',
                    marginLeft: '32px',
                    width: '100%'
                },
                '& input[type=text]': {
                    paddingTop: '9px',
                    paddingBottom: '10px',
                    paddingLeft: '14px',
                    marginLeft: '10px',
                    width: '100%'
                },
                '& .MuiInputBase-adornedStart': {
                    'width': 'calc(100% - 34px)',
                    '& input': {
                        width: '100%',
                        paddingLeft: '7px'
                    },
                    '& fieldset': {
                        top: '-3px'
                    }
                },
                '& fieldset': {
                    width: '100%',
                    top: '2px'
                }
            }
        },
        circle: {
            color: '000',
            [theme.breakpoints.down('md')]: {
                width: '14px',
                height: '14px'
            },
            [theme.breakpoints.only('lg')]: {
                width: '18px',
                height: '18px'
            },
            [theme.breakpoints.only('xl')]: {
                width: '24px',
                height: '24px'
            }
        },
        spayAndNeuter: {
            'display': 'flex',
            'justifyContent': 'space-between',
            'borderBottom': '1px solid #EFEFEF',
            [theme.breakpoints.down('sm')]: {
                marginBottom: '20px',
                borderBottom: 'none'
            },
            [theme.breakpoints.only('md')]: {
                marginBottom: '17px',
                borderBottom: 'none'
            },
            [theme.breakpoints.only('lg')]: {
                marginBottom: '22px'
            },
            [theme.breakpoints.only('xl')]: {
                marginBottom: '34px'
            },
            '& .MuiTypography-root': {
                fontWeight: 500,
                [theme.breakpoints.down('sm')]: {
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '100%',
                    marginBottom: '3px'
                },
                [theme.breakpoints.only('md')]: {
                    fontSize: '16px',
                    lineHeight: '15px',
                    marginBottom: '5px'
                },
                [theme.breakpoints.only('lg')]: {
                    fontSize: '16px',
                    lineHeight: '20px',
                    marginBottom: '7px'
                },
                [theme.breakpoints.only('xl')]: {
                    fontSize: '16px',
                    lineHeight: '30px',
                    marginBottom: '11px'
                }
            }
        },
        spayAndNeuterRow: {
            [theme.breakpoints.only('lg')]: {
                paddingBottom: '16px'
            },
            [theme.breakpoints.only('xl')]: {
                paddingBottom: '16px'
            }
        },
        manualApproveRow: {
            borderBottom: 'none',
            marginTop: '24px',
            [theme.breakpoints.only('lg')]: {
                marginTop: '28px'
            },
            [theme.breakpoints.only('xl')]: {
                marginTop: '32px'
            }
        },
        manualApproveDescription: {
            color: '#8C8C8C',
            fontSize: '13px !important',
            fontWeight: 400,
            lineHeight: '18px !important',
            marginTop: '6px !important',
            marginBottom: '0 !important'
        },
        typeContainer: {
            [theme.breakpoints.down('sm')]: {
                'width': '100%',
                '& > div:nth-last-child(2)': {
                    marginBottom: '10px'
                }
            },
            [theme.breakpoints.only('md')]: {
                width: '100%'
            }
        },
        separator: {
            display: 'none',
            backgroundColor: '#F4F3F0',
            height: '16px',
            width: '110%',
            marginLeft: '-5%',
            [theme.breakpoints.down('sm')]: {
                display: 'block'
            }
        },
        buttonContainerFull: {
            [theme.breakpoints.down('sm')]: {
                'width': '100%',
                'position': 'absolute',
                'bottom': '60px',
                'left': 0,
                'paddingLeft': '15px',
                'paddingRight': '15px',
                '& button': {
                    'width': '100%',
                    'height': '50px',
                    'borderRadius': '31px',
                    '& .MuiButton-label': {
                        fontSize: '16px'
                    }
                },
                '@supports (-webkit-touch-callout: none)': {
                    bottom: '75px'
                }
            }
        }
    })
);

export const PetRequirements: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const masterData = useMasterData();

    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const [activeSpecies, setActiveSpecies] = useState<keyof MarketplaceSpeciesRequirements>('dogs');
    const [requirements, setRequirements] = useState<MarketplaceSpeciesRequirements>(props.requirements);
    const [vaccinationsEnabled, setVaccinationsEnabled] = useState<
        Record<keyof MarketplaceSpeciesRequirements, boolean>
    >({
        dogs: props.requirements.dogs.requirements.length > 0 || props.requirements.dogs.otherRequirements.length > 0,
        cats: props.requirements.cats.requirements.length > 0 || props.requirements.cats.otherRequirements.length > 0
    });
    const [saved, setSaved] = useState(true);
    const [loading, setLoading] = useState(false);

    const activeRequirements = requirements[activeSpecies];
    const availableRequirements = masterData.speciesPetRequirements[activeSpecies].filter(
        requirement => !['spay_and_neuter', 'manually_approve_vaccines'].includes(requirement.id)
    );
    const activeVaccinationsEnabled = vaccinationsEnabled[activeSpecies];
    const showManuallyApproveVaccinesToggle = true;

    const getRequirementsToSave = (): MarketplaceSpeciesRequirements => ({
        dogs: vaccinationsEnabled.dogs
            ? requirements.dogs
            : { ...requirements.dogs, requirements: [], otherRequirements: [] },
        cats: vaccinationsEnabled.cats
            ? requirements.cats
            : { ...requirements.cats, requirements: [], otherRequirements: [] }
    });

    useNonInitialEffect(() => {
        setSaved(_.isEqual(props.requirements, getRequirementsToSave()));
    }, [requirements, vaccinationsEnabled]);

    useNonInitialEffect(() => {
        switch (status) {
            case MarketplaceStatus.SaveSuccess:
                setSaved(true);
                setLoading(false);
                break;
            case MarketplaceStatus.Error:
                setLoading(false);
        }
    }, [status]);

    const updateActiveRequirements = (updatedRequirements: MarketplaceRequirements) => {
        setRequirements(prev => ({
            ...prev,
            [activeSpecies]: updatedRequirements
        }));
    };

    const createRequirementOption = (name: string): Option<string> => ({
        id: name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, ''),
        name: name.trim()
    });

    const addOtherHandler = (value: string) => {
        const option = createRequirementOption(value);
        if (!activeRequirements.otherRequirements.some(other => other.id === option.id)) {
            updateActiveRequirements({
                ...activeRequirements,
                otherRequirements: [...activeRequirements.otherRequirements, option]
            });
        }
    };

    const toggleVaccinations = () => {
        setVaccinationsEnabled(prev => ({
            ...prev,
            [activeSpecies]: !prev[activeSpecies]
        }));
    };

    const saveHandler = () => {
        props.onSave && setLoading(true);
        props.onSave && props.onSave(getRequirementsToSave());
    };

    return (
        <Box className={clsx(classes.container, props.className, classes.containerNew)}>
            <Box className={classes.titleContainer}>
                <BusinessSettingsTitle
                    className={classes.businessSettingsTittle}
                    subtitle="Select the requirements needed for pets to attend your facility."
                >
                    {' '}
                    {props.isMobile && (
                        <FontAwesomeIcon
                            className={classes.iconBusinessTitle}
                            onClick={props.openSidebar}
                            icon={faArrowLeft}
                            size="2x"
                        />
                    )}{' '}
                    Pet Requirements
                </BusinessSettingsTitle>
            </Box>
            <Box className={clsx(classes.contentContainer, classes.contentContainerMobile)}>
                <Box className={classes.tabs}>
                    {(['dogs', 'cats'] as Array<keyof MarketplaceSpeciesRequirements>).map(species => (
                        <button
                            key={species}
                            type="button"
                            className={clsx(classes.tab, activeSpecies === species && classes.activeTab)}
                            onClick={() => setActiveSpecies(species)}
                        >
                            {_.capitalize(species)}
                        </button>
                    ))}
                </Box>

                <Box
                    className={clsx(classes.spayAndNeuter, classes.spayAndNeuterRow)}
                    onClick={() =>
                        updateActiveRequirements({
                            ...activeRequirements,
                            spayedAndNeureted: !activeRequirements.spayedAndNeureted
                        })
                    }
                >
                    <Typography>Spay and Neuter</Typography>
                    <Box onClick={event => event.stopPropagation()}>
                        <Switch
                            checked={activeRequirements.spayedAndNeureted}
                            onChange={() =>
                                updateActiveRequirements({
                                    ...activeRequirements,
                                    spayedAndNeureted: !activeRequirements.spayedAndNeureted
                                })
                            }
                        />
                    </Box>
                </Box>

                <Box className={classes.separator}></Box>

                <Accordion
                    withoutIcon
                    expanded={activeVaccinationsEnabled}
                    handleExpandedChange={toggleVaccinations}
                    title={'Vaccinations'}
                    titleStyle={classes.titleSection}
                    titleContainerStyles={`${classes.accordionTitle} ${classes.vaccinationsTitle}`}
                    expandComponent={
                        <Box onClick={event => event.stopPropagation()}>
                            <Switch
                                checked={activeVaccinationsEnabled}
                                onChange={toggleVaccinations}
                            />
                        </Box>
                    }
                >
                    <Box className={classes.typeContainer}>
                        {availableRequirements.map((requirement, index) => {
                            const selected = activeRequirements.requirements.some(req => req.id === requirement.id);
                            return (
                                <OptionType
                                    key={requirement.id}
                                    index={index}
                                    checked={selected}
                                    optionName={requirement.name}
                                    onTypeChange={(selected: boolean) => {
                                        updateActiveRequirements({
                                            ...activeRequirements,
                                            requirements: selected
                                                ? [...activeRequirements.requirements, requirement]
                                                : activeRequirements.requirements.filter(
                                                      req => req.id !== requirement.id
                                                  )
                                        });
                                    }}
                                />
                            );
                        })}
                        {activeRequirements.otherRequirements.map((other, index) => {
                            return (
                                <OptionType
                                    key={other.id}
                                    index={index}
                                    checked={true}
                                    editable
                                    optionName={other.name}
                                    onEditName={value =>
                                        updateActiveRequirements({
                                            ...activeRequirements,
                                            otherRequirements: activeRequirements.otherRequirements.map(
                                                (item, otherIndex) =>
                                                    otherIndex === index ? createRequirementOption(value) : item
                                            )
                                        })
                                    }
                                    onTypeChange={() =>
                                        updateActiveRequirements({
                                            ...activeRequirements,
                                            otherRequirements: activeRequirements.otherRequirements.filter(
                                                (_, otherIndex) => otherIndex !== index
                                            )
                                        })
                                    }
                                />
                            );
                        })}
                        <Box className={clsx(classes.addOtherContainer, classes.addOtherSpacing)}>
                            <AddOther
                                placeholderText={'Vaccine Name'}
                                onAddType={addOtherHandler}
                            />
                        </Box>
                    </Box>
                </Accordion>

                {showManuallyApproveVaccinesToggle && (
                    <Box
                        className={clsx(classes.spayAndNeuter, classes.manualApproveRow)}
                        onClick={() =>
                            updateActiveRequirements({
                                ...activeRequirements,
                                manuallyApproveVaccines: !activeRequirements.manuallyApproveVaccines
                            })
                        }
                    >
                        <Box>
                            <Typography>Manually Approve Vaccines</Typography>
                            <Typography className={classes.manualApproveDescription}>
                                Review and approve all vaccine updates before they&apos;re accepted.
                            </Typography>
                        </Box>
                        <Box onClick={event => event.stopPropagation()}>
                            <Switch
                                checked={activeRequirements.manuallyApproveVaccines}
                                onChange={() =>
                                    updateActiveRequirements({
                                        ...activeRequirements,
                                        manuallyApproveVaccines: !activeRequirements.manuallyApproveVaccines
                                    })
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Box>
            <Box className={clsx(classes.buttonContainer, classes.buttonContainerFull)}>
                <Button
                    label={saved ? 'Saved' : 'Save'}
                    onClick={saveHandler}
                    loading={loading}
                />
            </Box>
        </Box>
    );
};

export default PetRequirements;
